import { ErrorOutlineOutlined } from '@mui/icons-material';
import { Box, Skeleton, Typography } from '@mui/material';
import axios, { AxiosError } from 'axios';
import React from 'react';
import { Buffer } from 'buffer';
import { useTranslation } from 'react-i18next';

function CameraImage({
  snapUrl,
  alt,
  token,
  height = 231,
  refetchCamerasList,
}: {
  snapUrl: string;
  alt: string;
  token?: string;
  height?: number;
  refetchCamerasList: () => void;
}) {
  const { t } = useTranslation('cameras');
  const [loadingImage, setLoadingImage] = React.useState(true);
  const [errorLoadingImage, setErrorLoadingImage] = React.useState<AxiosError | null>(null);
  const [image64, setImage64] = React.useState<string | null>(null);

  React.useEffect(
    function loadImage() {
      async function load() {
        try {
          const data = await axios.get(snapUrl, {
            responseType: 'arraybuffer',
            headers: {
              Authorization: token || '',
            },
          });
          let resJSON;
          if (data.status === 200) {
            // Quando a câmera está offline retorna status 200, mas com mensagem de erro
            try {
              resJSON = JSON.parse(Buffer.from(data.data).toString()) as {
                Response: { Code: number; Message: string };
              };
            } catch (error) {}
            if (resJSON?.Response?.Code === 100 || resJSON?.Response?.Code === 4) {
              throw new Error(resJSON?.Response?.Message);
            }

            setImage64(Buffer.from(data.data).toString('base64'));
          } else {
            throw new Error(t('no_image_to_show'));
          }
        } catch (error) {
          setErrorLoadingImage(error as AxiosError);
          console.error(error);

          if ((error as AxiosError).message === 'Invalid or expired authentication session') {
            refetchCamerasList();
          }
        } finally {
          setLoadingImage(false);
        }
      }

      load();
    },
    [t, snapUrl, token, refetchCamerasList],
  );

  if (loadingImage) {
    return <CameraImage.Loading />;
  }

  if (errorLoadingImage) {
    return (
      <Box
        display="flex"
        flex="1 1 auto"
        alignItems={'center'}
        justifyContent="center"
        flexDirection={'column'}
      >
        <ErrorOutlineOutlined fontSize="large" />
        <Typography variant="caption" sx={{ mt: 0.5, textAlign: 'center' }}>
          {errorLoadingImage.message}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      component="img"
      src={`data:image/png;base64, ${image64}`}
      alt={alt}
      loading="lazy"
      sx={{
        height,
        maxWidth: '100%',
        borderRadius: '4px',
        flex: '1 1 auto',
      }}
    />
  );
}

CameraImage.Loading = function Loading() {
  return (
    <Skeleton
      height={'100%'}
      width={'100%'}
      sx={{ transform: 'none', ':before': { display: 'none' } }}
    />
  );
};

export default CameraImage;
